/**
 * The request page's states other than the form itself: not open yet (outside the pilot) and the
 * confirmation after sending. Split from SubmitRequestView so they can be rendered on their own.
 */
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Lock } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { SentReceipt } from '../../types/peopleTickets';
import { QUEUE_TITLES, RESPONSE_TIME } from './requestCopy';

export function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-[60vh] items-center justify-center p-6">{children}</div>;
}

export function NotOpenYet() {
  return (
    <Centered>
      <div className="w-full max-w-sm space-y-4 rounded border border-solid border-pearl bg-white p-8 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-cotton">
          <Lock className="h-6 w-6 text-iron" />
        </div>
        <h2 className="body-16-semibold text-black">Coming soon</h2>
        <p className="body-14-regular text-iron">
          The new People request form is being tested. Until it opens, please keep using the forms you use today.
        </p>
      </div>
    </Centered>
  );
}

function RecapRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-32 shrink-0 body-14-regular text-iron">{label}</dt>
      <dd className="body-16-regular text-black">{value}</dd>
    </div>
  );
}

/**
 * The confirmation. It repeats what was sent — so the employee can see it reached the team they
 * meant — and says who picks it up and by when, using the same response times the urgency
 * question promised on the form. Same page width and type scale as the form itself.
 */
export function Submitted({ receipt, onAnother }: { receipt: SentReceipt; onAnother: () => void }) {
  const navigate = useNavigate();
  const { ticket, attachmentsSent, attachmentsFailed } = receipt;
  const who = ticket.assignee?.name ?? 'The People team';
  const when = RESPONSE_TIME[ticket.priority] ?? RESPONSE_TIME.Regular;
  const uploaded = attachmentsSent - attachmentsFailed;

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="rounded border border-solid border-pearl bg-white p-8">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-9 w-9 shrink-0 text-green" aria-hidden="true" />
          <h1 className="text-2xl font-semibold text-black">Request sent</h1>
        </div>
        <p className="mt-4 body-14-regular text-iron">
          {ticket.ticketKey ? `Your reference is ${ticket.ticketKey}. ` : ''}
          {who} will pick it up — expect a reply {when}.
        </p>

        <dl className="mt-8 space-y-4 border-t border-solid border-pearl pt-6">
          {ticket.queue && <RecapRow label="Section" value={QUEUE_TITLES[ticket.queue] ?? ticket.queue} />}
          {ticket.category && <RecapRow label="Category" value={ticket.category} />}
          {ticket.subcategory && <RecapRow label="Detail" value={ticket.subcategory} />}
          <RecapRow label="Urgency" value={ticket.priority} />
          <RecapRow
            label="Attachments"
            value={attachmentsSent === 0 ? 'None' : `${uploaded} of ${attachmentsSent} uploaded`}
          />
        </dl>

        {attachmentsFailed > 0 && (
          // The ticket is filed either way; saying it failed would send someone back to file it twice.
          <p className="mt-6 rounded bg-orange/10 px-4 py-3 body-14-regular text-black">
            {attachmentsFailed === 1 ? 'One attachment' : `${attachmentsFailed} attachments`} could not be uploaded. Your
            request was still sent — reply with the file when the People team gets in touch.
          </p>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <Button className="rounded" onClick={() => navigate('/my-inquiries')}>
            Go to My inquiries
          </Button>
          <Button variant="outline" className="rounded" onClick={onAnother}>
            Send another request
          </Button>
        </div>
      </div>
    </div>
  );
}
